// src/cockpit4/components/SmartAdAssistant.tsx
// ============================================
// 智能投放助手（营销中心 P2-功能8）- v2.0 详细改进计划
// 投放预算智能分配 / 实时投放效果监控 / 自动出价建议 / ROI 预测 / A/B 测试
// 数据来源：localStorage（xeaos_smart_ad_campaigns）+ 内置规则引擎
// ============================================

import { useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Target, Megaphone, Wallet, FlaskConical, RefreshCw, Zap, CheckCircle2, AlertTriangle } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";

interface Campaign {
  id: string;
  name: string;
  platform: "公众号" | "小红书" | "抖音" | "知乎";
  status: "投放中" | "暂停" | "已完成";
  budget: number;
  spent: number;
  impressions: number;
  clicks: number;
  conversions: number;
  revenue: number;
}

const PLATFORM_COLORS: Record<string, string> = {
  公众号: "#38BDF8", 小红书: "#F472B6", 抖音: "#8B5CF6", 知乎: "#34D399",
};

const INITIAL_CAMPAIGNS: Campaign[] = [
  { id: "c1", name: "公众号-新品预热", platform: "公众号", status: "投放中", budget: 5000, spent: 3200, impressions: 82000, clicks: 4100, conversions: 86, revenue: 18600 },
  { id: "c2", name: "小红书-种草笔记", platform: "小红书", status: "投放中", budget: 4000, spent: 1800, impressions: 64000, clicks: 5200, conversions: 124, revenue: 24800 },
  { id: "c3", name: "抖音-信息流", platform: "抖音", status: "投放中", budget: 8000, spent: 6100, impressions: 210000, clicks: 9800, conversions: 210, revenue: 39200 },
  { id: "c4", name: "知乎-问答营销", platform: "知乎", status: "暂停", budget: 3000, spent: 2400, impressions: 45000, clicks: 2600, conversions: 45, revenue: 9800 },
];

const KEY = "xeaos_smart_ad_campaigns";

function loadCampaigns(): Campaign[] {
  try {
    const raw = localStorage.getItem(KEY);
    if (raw) return JSON.parse(raw) as Campaign[];
  } catch { /* ignore */ }
  return INITIAL_CAMPAIGNS;
}

function saveCampaigns(list: Campaign[]) {
  try { localStorage.setItem(KEY, JSON.stringify(list)); } catch { /* ignore */ }
}

export default function SmartAdAssistant() {
  const [campaigns, setCampaigns] = useState<Campaign[]>(loadCampaigns);
  const [selected, setSelected] = useState<string>("c2");

  const stats = useMemo(() => {
    const totalBudget = campaigns.reduce((a, c) => a + c.budget, 0);
    const totalSpent = campaigns.reduce((a, c) => a + c.spent, 0);
    const totalRevenue = campaigns.reduce((a, c) => a + c.revenue, 0);
    const totalClicks = campaigns.reduce((a, c) => a + c.clicks, 0);
    const totalConv = campaigns.reduce((a, c) => a + c.conversions, 0);
    const totalImp = campaigns.reduce((a, c) => a + c.impressions, 0);
    const ctr = totalImp > 0 ? (totalClicks / totalImp) * 100 : 0;
    const cvr = totalClicks > 0 ? (totalConv / totalClicks) * 100 : 0;
    const roi = totalSpent > 0 ? totalRevenue / totalSpent : 0;
    return { totalBudget, totalSpent, totalRevenue, ctr, cvr, roi };
  }, [campaigns]);

  // 预算分配建议（按当前 ROI 加权）
  const allocation = useMemo(() => {
    const sorted = [...campaigns].sort((a, b) => (b.revenue / Math.max(1, b.spent)) - (a.revenue / Math.max(1, a.spent)));
    const totalRoi = sorted.reduce((s, c) => s + c.revenue / Math.max(1, c.spent), 0);
    return sorted.map((c) => {
      const cRoi = c.revenue / Math.max(1, c.spent);
      const share = totalRoi > 0 ? Math.round((cRoi / totalRoi) * 100) : 0;
      return { ...c, suggested: Math.round(20000 * (share / 100)), share };
    });
  }, [campaigns]);

  const activeCampaign = campaigns.find((c) => c.id === selected) ?? campaigns[0];

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <Target size={14} className="text-pink-400" />
        <h3 className="text-[12px] font-medium text-white">智能投放助手</h3>
        <span className="text-[9px] font-mono text-gray-500 uppercase tracking-wider">P2-功能8 · Smart Ad</span>
        <span className="text-[8px] font-mono text-amber-500/70 px-1.5 py-0.5 rounded bg-amber-500/10" title="预置示例数据便于演示，可编辑/新增真实投放计划，数据保存在本地浏览器">示例数据可编辑</span>
        <button
          onClick={() => setCampaigns((prev) => {
            const next = prev.map((c) => (c.id === "c2" ? { ...c, spent: c.spent + 200, impressions: c.impressions + 5000, clicks: c.clicks + 260, conversions: c.conversions + 9, revenue: c.revenue + 1700 } : c));
            saveCampaigns(next);
            return next;
          })}
          className="flex items-center gap-1 ml-auto px-2 py-1 rounded-md border border-white/[0.06] bg-white/[0.02] text-[10px] text-gray-400 hover:text-white"
        >
          <RefreshCw size={10} /> 模拟刷新
        </button>
      </div>

      <div className="grid grid-cols-5 gap-2">
        <MetricCard label="总预算" value={`¥${stats.totalBudget.toLocaleString()}`} color="#94A3B8" />
        <MetricCard label="已消耗" value={`¥${stats.totalSpent.toLocaleString()}`} color="#F59E0B" />
        <MetricCard label="投放收入" value={`¥${stats.totalRevenue.toLocaleString()}`} color="#34D399" />
        <MetricCard label="整体 CTR" value={`${stats.ctr.toFixed(2)}%`} color="#38BDF8" />
        <MetricCard label="整体 ROI" value={`${stats.roi.toFixed(2)}x`} color="#A78BFA" />
      </div>

      <div className="grid grid-cols-12 gap-3">
        <div className="col-span-7 rounded-lg border border-white/[0.06] bg-white/[0.02] p-3">
          <div className="flex items-center justify-between mb-2">
            <span className="text-[10px] font-mono text-gray-400 uppercase tracking-wider">各投放计划 · 收入 / 花费</span>
            <Zap size={11} className="text-pink-400" />
          </div>
          <div className="h-[180px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={campaigns.map((c) => ({ name: c.platform, 收入: c.revenue, 花费: c.spent }))} margin={{ top: 5, right: 5, left: 0, bottom: 0 }}>
                <CartesianGrid stroke="#1e293b" strokeDasharray="3 3" />
                <XAxis dataKey="name" tick={{ fill: "#64748B", fontSize: 10, fontFamily: "monospace" }} />
                <YAxis tick={{ fill: "#64748B", fontSize: 10, fontFamily: "monospace" }} width={44} />
                <Tooltip contentStyle={{ background: "#0a0e1a", border: "1px solid #1e2a3f", borderRadius: 6, fontSize: 11, fontFamily: "monospace" }} labelStyle={{ color: "#E5E7EB" }} formatter={(v: any, n: any) => [`¥${Number(v).toLocaleString()}`, n]} />
                <Bar dataKey="收入" fill="#34D399" radius={[3, 3, 0, 0]} />
                <Bar dataKey="花费" fill="#F59E0B" radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="col-span-5 rounded-lg border border-white/[0.06] bg-white/[0.02] p-3">
          <div className="flex items-center justify-between mb-2">
            <span className="text-[10px] font-mono text-gray-400 uppercase tracking-wider">预算智能分配建议</span>
            <Wallet size={11} className="text-cyan-400" />
          </div>
          <div className="space-y-2">
            {allocation.map((c, i) => (
              <div key={c.id} className="flex items-center gap-2">
                <span className="w-14 text-[9px] font-mono text-gray-500 truncate">{c.platform}</span>
                <div className="flex-1 h-2 bg-white/10 rounded-full overflow-hidden">
                  <motion.div
                    className="h-full rounded-full"
                    initial={{ width: 0 }}
                    animate={{ width: `${c.share}%` }}
                    transition={{ duration: 0.6, delay: i * 0.06 }}
                    style={{ background: PLATFORM_COLORS[c.platform] }}
                  />
                </div>
                <span className="text-[9px] font-mono text-gray-300 w-16 text-right">¥{c.suggested.toLocaleString()}</span>
              </div>
            ))}
          </div>
          <p className="text-[9px] font-mono text-gray-600 mt-2">按 ROI 加权分配 · 高 ROI 渠道自动加码</p>
        </div>
      </div>

      <div className="rounded-lg border border-white/[0.06] bg-white/[0.02] p-3">
        <div className="flex items-center gap-2 mb-2">
          <Megaphone size={11} className="text-pink-400" />
          <span className="text-[10px] font-mono text-gray-400 uppercase tracking-wider">投放计划 · 效果与出价建议</span>
        </div>
        <div className="grid grid-cols-2 gap-2">
          {campaigns.map((c) => {
            const roi = c.spent > 0 ? c.revenue / c.spent : 0;
            const cvr = c.clicks > 0 ? (c.conversions / c.clicks) * 100 : 0;
            const isSel = c.id === selected;
            return (
              <button
                key={c.id}
                onClick={() => setSelected(c.id)}
                className={`rounded-lg border p-3 text-left transition-colors ${
                  isSel ? "border-pink-500/40 bg-pink-500/10" : "border-white/[0.06] bg-white/[0.02] hover:bg-white/[0.04]"
                }`}
              >
                <div className="flex items-center gap-2">
                  <span className="text-[10px] font-medium text-white truncate">{c.name}</span>
                  <span className="text-[9px] font-mono" style={{ color: PLATFORM_COLORS[c.platform] }}>{c.platform}</span>
                  <span className={`ml-auto text-[9px] font-mono px-1.5 py-0.5 rounded ${c.status === "投放中" ? "bg-emerald-500/15 text-emerald-400" : "bg-gray-500/15 text-gray-400"}`}>{c.status}</span>
                </div>
                <div className="flex items-center gap-3 mt-1.5 text-[9px] font-mono text-gray-500">
                  <span>花费 ¥{c.spent.toLocaleString()}/{c.budget.toLocaleString()}</span>
                  <span>转化 {c.conversions}</span>
                  <span className={roi >= 3 ? "text-emerald-400" : "text-amber-400"}>ROI {roi.toFixed(2)}x</span>
                  <span className="text-gray-600">CVR {cvr.toFixed(1)}%</span>
                </div>
                {isSel && (
                  <div className="mt-1.5 text-[9px] font-mono text-cyan-300">
                    建议出价：{(c.budget / Math.max(1, c.conversions) * 0.8).toFixed(1)} 元/转化（当前 {(c.spent / Math.max(1, c.conversions)).toFixed(1)} 元）
                    {roi < 2 && <span className="text-amber-400"> · 建议暂停或降价</span>}
                  </div>
                )}
              </button>
            );
          })}
        </div>
      </div>

      <div className="rounded-lg border border-white/[0.06] bg-white/[0.02] p-3">
        <div className="flex items-center gap-2 mb-2">
          <FlaskConical size={11} className="text-purple-400" />
          <span className="text-[10px] font-mono text-gray-400 uppercase tracking-wider">投放策略 A/B 测试建议</span>
        </div>
        <div className="grid grid-cols-3 gap-2 text-[9px] font-mono">
          {[
            { Icon: CheckCircle2, color: "#34D399", text: "抖音信息流 CTR 2.3% → 提高出价 10% 测试放量" },
            { Icon: AlertTriangle, color: "#FBBF24", text: "知乎问答转化偏低 → 建议 A/B 测试落地页文案" },
            { Icon: CheckCircle2, color: "#38BDF8", text: "小红书 ROI 6.9x → 建议预算加码 30%" },
          ].map((s, i) => (
            <div key={i} className="flex items-start gap-1.5 rounded-md border border-white/[0.04] bg-white/[0.01] p-2">
              <s.Icon size={10} style={{ color: s.color }} className="mt-0.5 flex-shrink-0" />
              <span className="text-gray-400 leading-relaxed">{s.text}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function MetricCard({ label, value, color }: { label: string; value: string; color: string }) {
  return (
    <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} className="rounded-lg border border-white/[0.06] bg-white/[0.02] px-3 py-2">
      <div className="text-[9px] font-mono text-gray-500 uppercase">{label}</div>
      <div className="text-sm font-semibold font-mono mt-0.5" style={{ color }}>{value}</div>
    </motion.div>
  );
}

